import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { UserRound, Plus, Building2, Search } from "lucide-react";
import api, { apiError } from "@/lib/crmApi";
import { toast } from "sonner";
import { Loader } from "@/crm/ui";

export default function Contacts() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [qDeb, setQDeb] = useState("");
  const [link, setLink] = useState("all");

  useEffect(() => { const t = setTimeout(() => setQDeb(q), 350); return () => clearTimeout(t); }, [q]);

  const load = useCallback(() => {
    const params = new URLSearchParams({ link });
    if (qDeb.trim()) params.set("q", qDeb.trim());
    api.get(`/contacts?${params}`).then(({ data: d }) => setData(d)).catch((e) => toast.error(apiError(e)));
  }, [qDeb, link]);

  useEffect(() => { load(); }, [load]);

  const createNew = async () => {
    try {
      const { data: c } = await api.post("/contacts", {});
      navigate(`/contacts/${c.id}`);
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-5" data-testid="contacts-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900 flex items-center gap-2">
            <UserRound size={22} className="text-indigo-500" /> Contacts
          </h1>
          <p className="text-sm text-slate-400">Contacts liés à une entreprise ou indépendants</p>
        </div>
        <button data-testid="new-contact-btn" onClick={createNew}
          className="flex items-center gap-2 px-4 py-2 rounded-xl bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700">
          <Plus size={16} /> Nouveau contact
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input data-testid="contacts-search" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Nom, téléphone, email…"
            className="pl-9 pr-3 py-2 rounded-lg border border-slate-300 text-sm w-64" />
        </div>
        <select data-testid="contacts-link-filter" value={link} onChange={(e) => setLink(e.target.value)}
          className="px-3 py-2 rounded-lg border border-slate-300 text-sm bg-white">
          <option value="all">Tous</option>
          <option value="linked">Liés à une entreprise</option>
          <option value="unlinked">Contacts libres</option>
        </select>
      </div>

      {!data ? <Loader /> : data.items.length === 0 ? (
        <p className="text-sm text-slate-400 text-center py-16">Aucun contact</p>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto">
          <table className="w-full text-sm" data-testid="contacts-table">
            <thead>
              <tr className="text-left text-xs text-slate-400 uppercase border-b border-slate-100">
                <th className="px-4 py-2.5">Nom</th><th className="px-4 py-2.5">Fonction</th>
                <th className="px-4 py-2.5">Téléphone</th><th className="px-4 py-2.5">Email</th>
                <th className="px-4 py-2.5">Entreprise</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((c) => (
                <tr key={c.id} data-testid={`contact-row-${c.id}`} onClick={() => navigate(`/contacts/${c.id}`)}
                  className="border-b border-slate-50 last:border-0 hover:bg-indigo-50/40 cursor-pointer">
                  <td className="px-4 py-2.5 font-medium text-slate-800">{[c.prenom, c.nom].filter(Boolean).join(" ") || "—"}</td>
                  <td className="px-4 py-2.5 text-slate-500">{c.fonction || "—"}</td>
                  <td className="px-4 py-2.5 font-mono text-xs text-slate-600">{c.telephone || "—"}</td>
                  <td className="px-4 py-2.5 text-slate-500">{c.email || "—"}</td>
                  <td className="px-4 py-2.5">
                    {c.company_nom ? (
                      <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 text-xs">
                        <Building2 size={11} /> {c.company_nom}
                      </span>
                    ) : <span className="text-xs text-slate-400">Contact libre</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
